import React, { useState, useEffect } from 'react';
import { X, XCircle, ClipboardList, Phone, Globe, Settings, Save, Plus } from 'lucide-react';
import { CustomPartner, CreateCustomPartnerRequest, UpdateCustomPartnerRequest, PartnerType, PartnerTypeLabels, PartnerTypeColors, validateCustomPartner } from '../../types/customPartners';

interface CustomPartnerFormProps {
  partner?: CustomPartner | null;
  onSubmit: (data: CreateCustomPartnerRequest | UpdateCustomPartnerRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const CustomPartnerForm: React.FC<CustomPartnerFormProps> = ({
  partner,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<CreateCustomPartnerRequest>({
    name: '',
    partnerType: 'other',
    description: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    logo: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Initialize form data when partner prop changes
  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name,
        partnerType: partner.partnerType,
        description: partner.description || '',
        website: partner.website || '',
        contactEmail: partner.contactEmail || '',
        contactPhone: partner.contactPhone || '',
        address: partner.address || '',
        logo: partner.logo || '',
        isActive: partner.isActive,
      });
    } else {
      setFormData({
        name: '',
        partnerType: 'other',
        description: '',
        website: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        logo: '',
        isActive: true,
      });
    }
    setErrors([]);
    setTouched({});
  }, [partner]);

  const handleInputChange = (field: keyof CreateCustomPartnerRequest, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateCustomPartner(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors([error instanceof Error ? error.message : 'Ein unbekannter Fehler ist aufgetreten']);
    }
  };

  const isEditing = !!partner;
  const title = isEditing ? 'Partner bearbeiten' : 'Neuen Partner erstellen';

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>{title}</h2>
          <button
            onClick={onCancel}
            style={closeButtonStyle}
            disabled={loading}
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {/* Error Display */}
          {errors.length > 0 && (
            <div style={errorContainerStyle}>
              <h4 style={errorTitleStyle}><XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Bitte korrigieren Sie folgende Fehler:</h4>
              <ul style={errorListStyle}>
                {errors.map((error, index) => (
                  <li key={index} style={errorItemStyle}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Basic Information */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}><ClipboardList size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Grundinformationen</h3>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                Name *
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={inputStyle}
                  placeholder="Name des Partners"
                  required
                  disabled={loading}
                />
              </label>

              <label style={labelStyle}>
                Partner-Typ *
                <select
                  value={formData.partnerType}
                  onChange={(e) => handleInputChange('partnerType', e.target.value)}
                  style={selectStyle}
                  required
                  disabled={loading}
                >
                  {Object.entries(PartnerTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label style={labelStyle}>
              Beschreibung
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={textareaStyle}
                placeholder="Beschreibung des Partners und der Zusammenarbeit"
                rows={3}
                disabled={loading}
              />
            </label>
          </div>

          {/* Contact Information */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}><Phone size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Kontaktinformationen</h3>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                E-Mail
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                  style={inputStyle}
                  placeholder="kontakt@partner.de"
                  disabled={loading}
                />
              </label>

              <label style={labelStyle}>
                Telefon
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  style={inputStyle}
                  placeholder="+49 123 456789"
                  disabled={loading}
                />
              </label>
            </div>

            <label style={labelStyle}>
              Adresse
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                style={textareaStyle}
                placeholder="Straße, PLZ Ort, Land"
                rows={2}
                disabled={loading}
              />
            </label>
          </div>

          {/* Additional Information */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}><Globe size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Zusätzliche Informationen</h3>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                Website
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  style={inputStyle}
                  placeholder="https://www.partner.de"
                  disabled={loading}
                />
              </label>

              <label style={labelStyle}>
                Logo URL
                <input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => handleInputChange('logo', e.target.value)}
                  style={inputStyle}
                  placeholder="https://www.partner.de/logo.png"
                  disabled={loading}
                />
              </label>
            </div>
          </div>

          {/* Status */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}><Settings size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Status</h3>
            
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                style={checkboxStyle}
                disabled={loading}
              />
              Partner ist aktiv
            </label>
          </div>

          {/* Form Actions */}
          <div style={actionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={submitButtonStyle}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span style={spinnerStyle}></span>
                  {isEditing ? 'Aktualisiere...' : 'Erstelle...'}
                </>
              ) : (
                isEditing ? <><Save size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Aktualisieren</> : <><Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Erstellen</>

              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  width: '100%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
  backgroundColor: '#f9fafb',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1f2937',
  margin: 0,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#6b7280',
  padding: '0.5rem',
  borderRadius: '6px',
  transition: 'background-color 0.2s',
};

const formStyle: React.CSSProperties = {
  padding: '1.5rem',
  overflow: 'auto',
  flex: 1,
};

const errorContainerStyle: React.CSSProperties = {
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  padding: '1rem',
  marginBottom: '1.5rem',
};

const errorTitleStyle: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '1rem',
  fontWeight: '600',
  margin: '0 0 0.5rem 0',
};

const errorListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: '1.5rem',
  color: '#dc2626',
};

const errorItemStyle: React.CSSProperties = {
  marginBottom: '0.25rem',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '1rem',
  paddingBottom: '0.5rem',
  borderBottom: '2px solid #e5e7eb',
};

const fieldGroupStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
  marginBottom: '1rem',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  backgroundColor: 'white',
  cursor: 'pointer',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
  fontFamily: 'inherit',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  fontSize: '1rem',
  fontWeight: '500',
  color: '#374151',
  cursor: 'pointer',
};

const checkboxStyle: React.CSSProperties = {
  width: '1.25rem',
  height: '1.25rem',
  cursor: 'pointer',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  paddingTop: '1.5rem',
  borderTop: '1px solid #e5e7eb',
  marginTop: '2rem',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: '500',
  color: '#374151',
  backgroundColor: 'white',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#10b981',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const spinnerStyle: React.CSSProperties = {
  width: '1rem',
  height: '1rem',
  border: '2px solid transparent',
  borderTop: '2px solid currentColor',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

export default CustomPartnerForm;
